import { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import {
  TrendingUp,
  BarChart3,
  PieChart as PieIcon,
  Users,
  Activity,
  Sparkles,
  PackageCheck,
} from 'lucide-react';
import { formatINR } from '@/shared/utils/currency';
import { useTopSellingProducts, type SalesOrder, type SaleReturn, type Shipment } from '../hooks/useSalesOpsApi';

interface SalesOpsAnalyticsProps {
  orders: SalesOrder[];
  returns: SaleReturn[];
  shipments: Shipment[];
}

const PIE_COLORS = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EC4899', '#06B6D4'];

function CustomChartTooltip({ active, payload, label, isCurrency = false }: any) {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-xl border border-slate-200/80 bg-white/95 p-3.5 shadow-xl backdrop-blur-md">
        <p className="text-xs font-bold text-slate-700 mb-1.5">{label}</p>
        <div className="space-y-1">
          {payload.map((entry: any, index: number) => (
            <div key={`item-${index}`} className="flex items-center justify-between gap-4 text-xs">
              <span className="flex items-center gap-1.5 font-medium" style={{ color: entry.color }}>
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
                {entry.name}:
              </span>
              <span className="font-bold tabular-nums text-slate-900">
                {isCurrency ? formatINR(entry.value) : entry.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

export function SalesOpsAnalytics({ orders }: SalesOpsAnalyticsProps) {
  const [analyticsView, setAnalyticsView] = useState<'revenue' | 'funnel' | 'docTypes' | 'topCustomers' | 'topProducts'>('revenue');
  const { data: topProducts = [], isLoading: topProductsLoading } = useTopSellingProducts();

  // 1. Monthly Revenue & Order Volume
  const revenueTrendData = useMemo(() => {
    const monthMap = new Map<string, { month: string; orderValue: number; orderCount: number; paidValue: number }>();
    
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      monthMap.set(key, { month: monthLabel, orderValue: 0, orderCount: 0, paidValue: 0 });
    }

    orders.forEach((o) => {
      if (!o.date) return;
      const key = o.date.slice(0, 7);
      const entry = monthMap.get(key);
      if (entry && o.status !== 'cancelled') {
        entry.orderValue += o.grandTotal || 0;
        entry.orderCount += 1;
        entry.paidValue += o.amountPaid || (o.status === 'paid' ? o.grandTotal : 0);
      }
    });

    return Array.from(monthMap.values());
  }, [orders]);

  // 2. Order Status Funnel
  const funnelData = useMemo(() => {
    const counts: Record<string, number> = {
      draft: 0,
      quotation: 0,
      confirmed: 0,
      fulfilled: 0,
      invoiced: 0,
      paid: 0,
    };

    orders.forEach((o) => {
      const s = o.status || 'draft';
      if (counts[s] !== undefined) counts[s] += 1;
    });

    return [
      { status: 'Draft / Quote', count: counts.draft + counts.quotation, fill: '#8B5CF6' },
      { status: 'Confirmed', count: counts.confirmed, fill: '#3B82F6' },
      { status: 'Fulfilled (Shipped)', count: counts.fulfilled, fill: '#06B6D4' },
      { status: 'Invoiced', count: counts.invoiced, fill: '#F59E0B' },
      { status: 'Paid', count: counts.paid, fill: '#10B981' },
    ];
  }, [orders]);

  // 3. Document Type Split
  const docTypeData = useMemo(() => {
    const map = new Map<string, number>();
    orders.forEach((o) => {
      const type = (o.documentType || 'sales_order').replace('_', ' ');
      map.set(type, (map.get(type) || 0) + 1);
    });
    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }, [orders]);

  // 4. Top Customers by Order Volume
  const topCustomerData = useMemo(() => {
    const map = new Map<string, number>();
    orders.forEach((o) => {
      const customer = o.customerId?.name || o.customerName || 'Walk-in / Direct';
      map.set(customer, (map.get(customer) || 0) + (o.grandTotal || 0));
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, value]) => ({ name, value }));
  }, [orders]);

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
      {/* Header & Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-500 text-white shadow-sm shadow-indigo-200">
            <Activity className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
              Sales Operations Analytics & Fulfillment Flow
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-data-xs font-bold text-blue-700 border border-blue-100">
                <Sparkles className="h-2.5 w-2.5" /> Pipeline Live
              </span>
            </h3>
            <p className="text-[12px] text-slate-500">
              Track quotation velocity, order conversion, fulfillment pipeline & customer revenue
            </p>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-1 rounded-xl bg-slate-100/80 p-1">
          <button
            type="button"
            onClick={() => setAnalyticsView('revenue')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
              analyticsView === 'revenue' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <TrendingUp className="h-3.5 w-3.5" /> Revenue Trajectory
          </button>
          <button
            type="button"
            onClick={() => setAnalyticsView('funnel')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
              analyticsView === 'funnel' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <BarChart3 className="h-3.5 w-3.5" /> Order Funnel
          </button>
          <button
            type="button"
            onClick={() => setAnalyticsView('docTypes')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
              analyticsView === 'docTypes' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <PieIcon className="h-3.5 w-3.5" /> Document Types
          </button>
          <button
            type="button"
            onClick={() => setAnalyticsView('topCustomers')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
              analyticsView === 'topCustomers' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Users className="h-3.5 w-3.5" /> Top Accounts
          </button>
          <button
            type="button"
            onClick={() => setAnalyticsView('topProducts')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
              analyticsView === 'topProducts' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <PackageCheck className="h-3.5 w-3.5" /> Top Selling Products
          </button>
        </div>
      </div>

      {/* Chart Canvas */}
      <div className="pt-4">
        {/* VIEW 1: REVENUE & ORDER VALUE AREA CHART */}
        {analyticsView === 'revenue' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-slate-600">Monthly Order Revenue vs Collections (Last 6 Months)</p>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1 font-medium text-indigo-600">
                  <span className="h-2.5 w-2.5 rounded-full bg-indigo-500" /> Order Book Value
                </span>
                <span className="flex items-center gap-1 font-medium text-emerald-600">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Collections / Paid
                </span>
              </div>
            </div>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueTrendData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorOrderVal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366F1" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#6366F1" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="colorPaidVal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} tickLine={false} axisLine={{ stroke: '#CBD5E1' }} />
                  <YAxis
                    tickFormatter={(val) => `₹${val >= 100000 ? `${(val / 100000).toFixed(1)}L` : `${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}`}
                    tick={{ fontSize: 11, fill: '#64748B' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip content={<CustomChartTooltip isCurrency />} />
                  <Area type="monotone" dataKey="orderValue" name="Order Book Value" stroke="#6366F1" strokeWidth={2.5} fillOpacity={1} fill="url(#colorOrderVal)" />
                  <Area type="monotone" dataKey="paidValue" name="Paid / Collected" stroke="#10B981" strokeWidth={2} fillOpacity={1} fill="url(#colorPaidVal)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* VIEW 2: ORDER FUNNEL BAR CHART */}
        {analyticsView === 'funnel' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-slate-600">Order Lifecycle Funnel (From Quotation to Payment)</p>
            </div>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnelData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="status" tick={{ fontSize: 11, fill: '#334155', fontWeight: 600 }} tickLine={false} axisLine={{ stroke: '#CBD5E1' }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748B' }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomChartTooltip />} />
                  <Bar dataKey="count" name="Document Count" radius={[6, 6, 0, 0]} maxBarSize={48}>
                    {funnelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* VIEW 3: DOCUMENT TYPE DONUT CHART */}
        {analyticsView === 'docTypes' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <div className="h-64 w-full flex items-center justify-center">
              {docTypeData.length === 0 ? (
                <div className="text-xs text-slate-400 font-medium">No sales documents recorded yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={docTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {docTypeData.map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="space-y-2 max-h-56 overflow-y-auto pr-2">
              <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Document Distribution</p>
              {docTypeData.map((entry, idx) => {
                const color = PIE_COLORS[idx % PIE_COLORS.length];
                const total = docTypeData.reduce((sum, item) => sum + item.value, 0);
                const percent = total > 0 ? Math.round((entry.value / total) * 100) : 0;
                return (
                  <div key={entry.name} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 border border-slate-100">
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
                      <span className="text-xs font-medium text-slate-800 capitalize">{entry.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-900">{entry.value}</span>
                      <span className="text-data-xs font-semibold text-slate-400">({percent}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* VIEW 4: TOP CUSTOMERS HORIZONTAL BAR CHART */}
        {analyticsView === 'topCustomers' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-slate-600">Leading Accounts by Order Value (₹)</p>
            </div>
            {topCustomerData.length === 0 ? (
              <div className="flex h-56 items-center justify-center text-xs text-slate-400 font-medium">
                No customer orders recorded yet.
              </div>
            ) : (
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topCustomerData} layout="vertical" margin={{ top: 5, right: 30, left: 60, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
                    <XAxis
                      type="number"
                      tickFormatter={(val) => `₹${val >= 100000 ? `${(val / 100000).toFixed(1)}L` : `${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}`}
                      tick={{ fontSize: 11, fill: '#64748B' }}
                      tickLine={false}
                    />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#334155', fontWeight: 600 }} tickLine={false} axisLine={false} width={120} />
                    <Tooltip content={<CustomChartTooltip isCurrency />} />
                    <Bar dataKey="value" name="Total Order Value" fill="#6366F1" radius={[0, 6, 6, 0]} barSize={16} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* VIEW 5: TOP SELLING PRODUCTS */}
        {analyticsView === 'topProducts' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-slate-600">
                Top Products by Volume Sold & Revenue — Know which products are selling more
              </p>
              <span className="text-data-xs text-slate-400 font-medium">Ranked by Total Quantity</span>
            </div>

            {topProductsLoading ? (
              <div className="flex h-56 items-center justify-center text-xs text-slate-400">Loading top products...</div>
            ) : topProducts.length === 0 ? (
              <div className="flex h-56 items-center justify-center text-xs text-slate-400">No product sales recorded yet.</div>
            ) : (
              <div className="space-y-4">
                {/* Bar chart of top 8 products by quantity */}
                <div className="h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={topProducts.slice(0, 8).map((p) => ({
                        name: p.productName.length > 20 ? `${p.productName.slice(0, 18)}…` : p.productName,
                        fullName: p.productName,
                        quantity: p.totalQuantity,
                        revenue: p.totalAmount,
                        unit: p.unit,
                      }))}
                      margin={{ top: 10, right: 20, left: 10, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748B' }} interval={0} angle={-15} textAnchor="end" />
                      <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                      <Tooltip
                        formatter={(val: any, name: string) => [
                          name === 'Quantity' ? `${val} units` : formatINR(val),
                          name,
                        ]}
                      />
                      <Bar dataKey="quantity" name="Quantity" fill="#3B82F6" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Detailed Table */}
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-data-xs font-bold uppercase text-slate-500 border-b border-slate-200">
                      <tr>
                        <th className="py-2.5 px-3">Rank</th>
                        <th className="py-2.5 px-3">Product Name</th>
                        <th className="py-2.5 px-3">SKU / Code</th>
                        <th className="py-2.5 px-3 text-right">Qty Sold</th>
                        <th className="py-2.5 px-3 text-right">Orders</th>
                        <th className="py-2.5 px-3 text-right">Total Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {topProducts.map((p, idx) => (
                        <tr key={p.productId || idx} className="hover:bg-slate-50 transition-colors">
                          <td className="py-2 px-3 font-bold text-slate-500">#{idx + 1}</td>
                          <td className="py-2 px-3 font-semibold text-slate-900">{p.productName}</td>
                          <td className="py-2 px-3 text-slate-500">{p.productCode || '—'}</td>
                          <td className="py-2 px-3 text-right font-bold text-blue-700">
                            {p.totalQuantity.toLocaleString('en-IN')} {p.unit}
                          </td>
                          <td className="py-2 px-3 text-right text-slate-600">{p.orderCount}</td>
                          <td className="py-2 px-3 text-right font-bold text-slate-900">
                            {formatINR(p.totalAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
